import { prepareTooltipRows, findTooltipRow } from '../js/tooltipRows';
import { toNumber, toNumberTimestamp } from '../../../util.js';
import { convertToZonedTimestamp } from '../../../js/helpers/date-helpers/dateHelpers';
import { useEffect, useMemo, useState } from 'react';
import type { RefObject } from 'react';
import type { ResolvedAxisChartSettings } from './useGeneratedAxisChart.types';

/** Track the pointer, then bisect actual visible samples using the current axis scale. */
const emptyData: Record<string, unknown>[] = [];

export const useGeneratedAxisTooltip = (tooltip_ref: RefObject<HTMLDivElement | null>,
  axes: ResolvedAxisChartSettings['axes'], data = emptyData, time_zone?: string) => {
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);
  useEffect(() => {
    const plot = tooltip_ref.current;
    if (!plot) return;
    const update = (event: PointerEvent) => {
      const bounds = plot.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      const clamp = (value: number) => Math.max(0, Math.min(100, value));
      setPointer({ x: clamp((event.clientX - bounds.left) / bounds.width * 100),
        y: clamp((event.clientY - bounds.top) / bounds.height * 100) });
    };
    const leave = () => setPointer(null);
    plot.addEventListener('pointerenter', update);
    plot.addEventListener('pointermove', update);
    plot.addEventListener('pointerleave', leave);
    return () => {
      plot.removeEventListener('pointerenter', update);
      plot.removeEventListener('pointermove', update);
      plot.removeEventListener('pointerleave', leave);
    };
  }, [tooltip_ref]);

  const horizontal = axes.bottom ?? axes.top;
  const anchor = horizontal ?? axes.left ?? axes.right;
  const anchor_key = anchor?.value_key;
  const rows = useMemo(() => anchor_key ? prepareTooltipRows(data, anchor_key, anchor?.scale) : [],
    [data, anchor_key, anchor?.scale]);
  const row = pointer ? findTooltipRow(rows, horizontal ? pointer.x : pointer.y) : undefined;
  const values = (['left', 'right', 'top', 'bottom'] as const).flatMap(side => {
    const axis = axes[side];
    if (!row || !axis) return [];
    const key = axis.value_key;
    const keys = axis.value_keys ?? (key ? [key] : []);
    return keys.flatMap(key => {
      const raw = row[key];
      const value = axis.scale_type === 'time' ? toNumberTimestamp(raw)
        : axis.scale_type === 'point' ? raw : toNumber(raw);
      if (value === undefined || value === null) return [];
      const default_text = axis.scale_type === 'time' ? convertToZonedTimestamp(value as number, time_zone, 'full')
        : typeof value === 'number' ? value.toLocaleString('en-US', { maximumFractionDigits: 2 }) : String(value);
      const text = axis.format?.({ value: value as string | number | Date, position: `${axis.scale?.(value) ?? 0}%` }, 'tooltip', { ...axis, time_zone })
        ?? default_text;
      return [{ side, key, label: keys.length > 1 ? key : axis.label ?? key, text, unit: axis.unit }];
    });
  });
  return { values, pointer, row };
};
